// components/TestRunner/TestRunner.js
import "./TestRunner.css";

import DataCollectorForTests from "../../utils/DataCollectorForTests.js";
import { ExcelDataPreparation } from "../../utils/ExcelDataPreparation.js";

// Import des rules metier

import { Rule00_BasicConfigurationFlags } from "./rules/Rule00_BasicConfigurationFlags.js";
import { Rule01_FiberFree_Report } from "./rules/Rule01_FiberFree_Report.js";
import { Rule02_FiberOccupied_NoReport } from "./rules/Rule02_FiberOccupied_NoReport.js";
import { Rule03_Measurement_FreeOnly } from "./rules/Rule03_Measurement_FreeOnly.js";
import { Rule04_FiberCategory_Exclusivity } from "./rules/Rule04_FiberCategory_Exclusivity.js";
//import { Rule05_PboConsistencyCheck } from "./rules/Rule05_PboConsistencyCheck.js";
import { Rule06a_SOR_NamingStandard_Success } from "./rules/Rule06a_SOR_NamingStandard_Success.js";
import { Rule06b_SOR_NamingStandard_Failure } from "./rules/Rule06b_SOR_NamingStandard_Failure.js";
import { Rule06c_SOR_NamingStandard_HorsService } from "./rules/Rule06c_SOR_NamingStandard_HorsService.js";
import { Rule07a_SOR_AntiCheat_NameVsMeta_Success } from "./rules/Rule07a_SOR_AntiCheat_NameVsMeta_Success.js";
import { Rule07b_SOR_AntiCheat_NameVsMeta_Failure } from "./rules/Rule07b_SOR_AntiCheat_NameVsMeta_Failure.js";
import { Rule07c_SOR_AntiCheat_NameVsMeta_HorsService } from "./rules/Rule07c_SOR_AntiCheat_NameVsMeta_HorsService.js";
import { Rule08_PBOGroup_Distance_EventsConsistency } from "./rules/Rule08_PBOGroup_Distance_EventsConsistency.js";
import { Rule09_PBOGroup_PulseLengthConsistency } from "./rules/Rule09_PBOGroup_PulseLengthConsistency.js";
import { Rule10_SOR_RefractiveIndex } from "./rules/Rule10_SOR_RefractiveIndex.js";

// autres imports
import { HTMLReportGenerator } from "../../utils/HTMLReportGenerator/HTMLReportGenerator.js";
import { ReportSuccessMessage } from "../ReportSuccessMessage/ReportSuccessMessage.js";
import { excelEnricherService } from "../../utils/ExcelEnricherService.js";

import logger from "../../utils/logger.js";

import { smartPmBackend } from "../../utils/SmartPmBackendService.js";

import { ExcelExportProgress } from "../ExcelExportProgress/ExcelExportProgress.js";

export class TestRunner {
  constructor(state) {
    this.state = state;
    this.dataCollector = new DataCollectorForTests();
    this.element = null;
    this.isRunning = false;
    this.isExporting = false;
    this.successMessage = null;
    this.generatedFileName = null;
    this.reportSize = null;
    this.pythonServiceStatus = false;
    this.exportProgress = new ExcelExportProgress();
  }

  async checkPythonService() {
    try {
      const health = await smartPmBackend.checkHealth();
      this.pythonServiceStatus = health.healthy;
      return health.healthy;
    } catch (error) {
      logger.error(
        {
          context: "health-check",
          error: error.message,
          stack: error.stack,
        },
        "Erreur health check service Python"
      );
      this.pythonServiceStatus = false;
      return false;
    }
  }

  hasData() {
    const stats = this.dataCollector.getStats();
    return stats.totalFibers > 0;
  }

  async executeTests() {
    if (this.isRunning) return;

    try {
      this.isRunning = true;
      this._updateUI("running");

      // Collecter et preparer les données pour les fibres.
      const fibers = this.dataCollector.collectAndPrepare();

      // Extraire le numero SRO-BPI de la base ROP
      const sroBpiPrefix = this._extractSroBpiPrefixes();

      // Stocker pour utilisation dans les règles
      this.sroBpiPrefix = sroBpiPrefix;

      if (fibers.length === 0) {
        throw new Error("Aucune donnée de fibre disponible pour générer le rapport");
      }

      // rules to be applyed to all fibers at once
      Rule00_BasicConfigurationFlags.apply(fibers);

      fibers.forEach((fiber) => {
        // rules to be applyed to fibers one by one
        Rule01_FiberFree_Report.apply(fiber);
        Rule02_FiberOccupied_NoReport.apply(fiber);
        Rule03_Measurement_FreeOnly.apply(fiber);
        Rule04_FiberCategory_Exclusivity.apply(fiber);
        Rule06a_SOR_NamingStandard_Success.apply(fiber);
        Rule06b_SOR_NamingStandard_Failure.apply(fiber);
        Rule06c_SOR_NamingStandard_HorsService.apply(fiber);
        Rule07a_SOR_AntiCheat_NameVsMeta_Success.apply(fiber);
        Rule07b_SOR_AntiCheat_NameVsMeta_Failure.apply(fiber);
        Rule07c_SOR_AntiCheat_NameVsMeta_HorsService.apply(fiber);
      });

      // rules to be applyed to all fibers at once
      //Rule05_PboConsistencyCheck.apply(fibers);
      Rule08_PBOGroup_Distance_EventsConsistency.apply(fibers);
      Rule09_PBOGroup_PulseLengthConsistency.apply(fibers);
      Rule10_SOR_RefractiveIndex.apply(fibers);

      const results = this._calculateResults(fibers);
      const htmlReport = HTMLReportGenerator.generate(results, fibers);
      this.reportSize = this._calculateFileSize(htmlReport);
      this._downloadReport(htmlReport);

      this._updateUI("completed", results);
    } catch (error) {
      logger.error(
        {
          context: "test-execution",
          error: error.message,
          stack: error.stack,
        },
        "❌ Erreur lors des tests"
      );
      this._updateUI("error", null, error.message);
    } finally {
      this.isRunning = false;
    }
  }

  async executeExcelExport() {
    if (this.isExporting || this.isRunning) return;

    try {
      this.isExporting = true;

      // Afficher la barre de progression
      this.exportProgress.show();

      // 1. Initialiser le service de préparation des données
      const excelPrep = new ExcelDataPreparation(this.state);

      // 2. Préparer toutes les données enrichies
      const enrichedData = excelPrep.export();

      // 3. Récupérer le fichier original BaseROP
      const baseRopParams = this.state.getBaseRopWorkBookParams();
      if (!baseRopParams || !baseRopParams.originalFile) {
        throw new Error("Fichier Excel original non trouvé. Veuillez recharger le fichier BaseROP.");
      }

      // 4. Préparer les données pour le service backend
      const backendData = {
        enrichedFibers: enrichedData.data,
        originalFile: baseRopParams.originalFile,
        worksheetName: baseRopParams.sheet,
        referenceColumn: baseRopParams.referenceColumn,
        portColumn: baseRopParams.portColumn,
        bpoColumn: baseRopParams.bpoColumn,
        timestamp: enrichedData.timestamp,
        version: "v2.0",
      };

      // 5. Appeler le service d'enrichissement avec callback de progression
      const result = await excelEnricherService.enrichBaseRop(backendData, (message) => {
        this._updateExportProgress(message);
      });

      // Marquer comme terminé
      this.exportProgress.complete(`Fichier ${result.fileName} téléchargé avec succès`);

      // Afficher le message de succès dans l'UI
      this._updateUI("export-completed", result);
    } catch (error) {
      logger.error(
        {
          context: "excel-export",
          error: error.message,
          stack: error.stack,
        },
        "❌ Erreur lors de l'export Excel"
      );

      // Marquer comme erreur
      this.exportProgress.error(error.message);

      this._updateUI("export-error", null, error.message);
    } finally {
      this.isExporting = false;
    }
  }

  _updateExportProgress(message) {
    // Extraire ou estimer le pourcentage
    const percentage = ExcelExportProgress.mapMessageToPercentage(message);

    // Mettre à jour la barre de progression
    this.exportProgress.update(message, percentage);
  }

  _calculateResults(fibers) {
    let totalOK = 0;
    let totalWarnings = 0;
    let totalErrors = 0;

    fibers.forEach((fiber) => {
      // Utiliser la même logique que ReportUtils.determineEffectiveStatus
      const messages = fiber.testResults?.messages || [];
      const hasErrors = messages.some((msg) => msg.type === "error");
      const hasWarnings = messages.some((msg) => msg.type === "warning");

      if (hasErrors) {
        fiber.testResults.status = "error";
        totalErrors++;
      } else if (hasWarnings) {
        fiber.testResults.status = "warning";
        totalWarnings++;
      } else {
        fiber.testResults.status = "ok";
        totalOK++;
      }
    });

    return {
      totalFibers: fibers.length,
      totalOK,
      totalWarnings,
      totalErrors,
      timestamp: new Date().toISOString(),
    };
  }

  _calculateFileSize(htmlContent) {
    const sizeInBytes = new Blob([htmlContent]).size;

    if (sizeInBytes < 1024) {
      return `${sizeInBytes} B`;
    } else if (sizeInBytes < 1024 * 1024) {
      return `${(sizeInBytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(sizeInBytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  }

  _downloadReport(htmlContent) {
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    this.generatedFileName = `rapport-tests-metiers-${timestamp}.html`;

    const blob = new Blob([htmlContent], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = this.generatedFileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Replace the _updateUI() method in TestRunner.js with this:
  _updateUI(state, results = null, errorMessage = null) {
    if (!this.element) return;

    const button = this.element.querySelector(".execute-btn");
    const exportButton = this.element.querySelector(".export-excel-btn");
    const status = this.element.querySelector(".progress-status");
    const resultsDiv = this.element.querySelector(".test-results");

    switch (state) {
      case "running":
        if (button) {
          button.disabled = true;
          button.textContent = "🔄 Génération en cours...";
        }
        if (exportButton) {
          exportButton.disabled = true;
        }
        if (status) {
          status.textContent = "Analyse des données et génération du rapport...";
          status.className = "progress-status running";
        }
        if (resultsDiv) {
          resultsDiv.innerHTML = "";
        }
        if (this.successMessage) {
          this.successMessage.unmount();
          this.successMessage = null;
        }
        break;

      case "exporting":
        // État pendant l'export Excel - on désactive les boutons
        if (button) {
          button.disabled = true;
        }
        if (exportButton) {
          exportButton.disabled = true;
          exportButton.textContent = "⏳ Export en cours...";
        }
        if (status) {
          status.textContent = "Export Excel en cours...";
          status.className = "progress-status running";
        }
        break;

      case "export-completed":
        // Export terminé avec succès
        if (button) {
          button.disabled = false;
        }
        if (exportButton) {
          exportButton.disabled = false;
          exportButton.textContent = "📊 Exporter Base ROP enrichie";
        }
        if (status) {
          status.textContent = `✅ Export terminé : ${results.fileName}`;
          status.className = "progress-status completed";
        }

        // Optionnel : Afficher un message de succès
        if (resultsDiv) {
          setTimeout(() => {
            if (status) {
              status.textContent = this.pythonServiceStatus ? "Prêt à générer" : "Génération HTML disponible";
              status.className = "progress-status";
            }
          }, 5000); // Effacer le message après 5 secondes
        }
        break;

      case "export-error":
        // Erreur pendant l'export
        if (button) {
          button.disabled = false;
        }
        if (exportButton) {
          exportButton.disabled = false;
          exportButton.textContent = "📊 Exporter Base ROP enrichie";
        }
        if (status) {
          status.textContent = `❌ Erreur export : ${errorMessage}`;
          status.className = "progress-status error";
        }

        // Effacer le message d'erreur après quelques secondes
        setTimeout(() => {
          if (status) {
            status.textContent = this.pythonServiceStatus ? "Prêt à générer" : "Génération HTML disponible";
            status.className = "progress-status";
          }
        }, 5000);
        break;

      case "completed":
        if (button) {
          button.disabled = false;
          button.textContent = "📋 Générer le rapport HTML";
        }
        if (exportButton) {
          exportButton.disabled = !this.pythonServiceStatus;
          exportButton.textContent = "📊 Exporter Base ROP enrichie";
        }
        if (status) {
          status.textContent = "Rapport généré avec succès";
          status.className = "progress-status completed";
        }

        this.successMessage = new ReportSuccessMessage({
          fileName: this.generatedFileName,
          fileSize: this.reportSize,
          timestamp: new Date().toLocaleString("fr-FR"),
          stats: {
            totalFibers: results.totalFibers,
            totalOK: results.totalOK,
            totalWarnings: results.totalWarnings,
            totalErrors: results.totalErrors,
          },
          onRegenerate: null,
        });

        if (resultsDiv) {
          resultsDiv.innerHTML = '<div id="success-message-container"></div>';
          setTimeout(() => {
            this.successMessage.mount("#success-message-container");
          }, 0);
        }
        break;

      case "error":
        if (button) {
          button.disabled = false;
          button.textContent = "📋 Générer le rapport HTML";
        }
        if (exportButton) {
          exportButton.disabled = !this.pythonServiceStatus;
          exportButton.textContent = "📊 Exporter Base ROP enrichie";
        }
        if (status) {
          status.textContent = `Erreur: ${errorMessage}`;
          status.className = "progress-status error";
        }
        if (resultsDiv) {
          resultsDiv.innerHTML = "";
        }
        if (this.successMessage) {
          this.successMessage.unmount();
          this.successMessage = null;
        }
        break;

      default:
        if (button) {
          button.disabled = false;
          button.textContent = "📋 Générer le rapport HTML";
        }
        if (exportButton) {
          exportButton.disabled = !this.pythonServiceStatus;
          exportButton.textContent = "📊 Exporter Base ROP enrichie";
        }
        if (status) {
          status.textContent = this.pythonServiceStatus ? "Prêt à générer" : "Génération HTML disponible";
          status.className = "progress-status";
        }
        if (resultsDiv) {
          resultsDiv.innerHTML = "";
        }
        if (this.successMessage) {
          this.successMessage.unmount();
          this.successMessage = null;
        }
    }
  }

  _extractSroBpiPrefixes() {
    try {
      const sources = this.state.getAllSources();
      const references = sources.baseROP?.data?.references;

      if (!references) return null;

      const prefixes = new Set();
      const pattern = /PBO-SRO-BPI-(\d+)/;

      references.forEach((ref) => {
        const pboRaw = ref.PBO_RAW;
        if (pboRaw) {
          const match = pboRaw.match(pattern);
          if (match) {
            // Normaliser : trim + uppercase
            const normalizedPrefix = `SRO-BPI-${match[1]}`.trim().toUpperCase();
            prefixes.add(normalizedPrefix);
          }
        }
      });

      const uniquePrefixes = Array.from(prefixes);

      // Vérifier qu'ils sont tous identiques
      if (uniquePrefixes.length === 0) {
        return null; // Aucun préfixe trouvé
      } else if (uniquePrefixes.length === 1) {
        return uniquePrefixes[0]; // Un seul préfixe unique
      } else {
        logger.warn(
          {
            context: "prefix-validation",
            prefixes: uniquePrefixes,
          },
          "Plusieurs préfixes SRO-BPI différents détectés"
        );
        return uniquePrefixes[0]; // Retourner le premier en cas de conflit
      }
    } catch (error) {
      logger.error(
        {
          context: "prefix-extraction",
          error: error.message,
          stack: error.stack,
        },
        "Erreur extraction préfixe SRO-BPI"
      );
      return null;
    }
  }

  renderNoDataState() {
    return `
      <div class="no-data-state">
        <div class="card-icon">❌</div>
        <h2 class="card-title">Aucune donnée disponible</h2>
        <p class="card-description">
          Veuillez d'abord charger toutes les sources de données dans les sections précédentes 
          pour pouvoir générer un rapport complet.
        </p>
        <div class="card-actions">
          <button class="btn btn-primary" onclick="window.app?.state?.setCurrentPage('config-global')">
            🔧 Aller à la configuration
          </button>
        </div>
      </div>
    `;
  }

  renderReadyState() {
    return `
    <div class="content-card">
      <div class="card-header">
        <div class="card-icon">⚡</div>
        <h2 class="title-md">Données prêtes</h2>
      </div>
      <p class="card-description">
        Vos données sont prêtes. Vous pouvez générer le rapport de validation ou exporter votre fichier Excel enrichi.
      </p>

      <div class="card-actions">
        <button class="btn btn-primary execute-btn">
          📋 Générer le rapport HTML
        </button>
        
        <button class="btn btn-secondary export-excel-btn" ${!this.pythonServiceStatus ? "disabled" : ""}>
          📊 Exporter Base ROP enrichie
        </button>
        
        <div class="service-status">
          <span class="service-indicator ${this.pythonServiceStatus ? "online" : "offline"}">
            ${this.pythonServiceStatus ? "🟢" : "🔴"}
          </span>
          <span class="service-text">
            (Service Excel ${this.pythonServiceStatus ? "disponible)" : "indisponible)"}
          </span>
        </div>
      </div>      
      
      <div class="progress-status">
        ${this.pythonServiceStatus ? "Prêt à générer" : "Génération HTML disponible"}
      </div>
    </div>

    <div class="test-results"></div>
  `;
  }

  render() {
    const hasData = this.hasData();

    return `
      <div class="test-runner">
        ${!hasData ? this.renderNoDataState() : this.renderReadyState()}
      </div>
    `;
  }

  async mount(container) {
    // Vérifier le service Python au montage
    await this.checkPythonService();

    this.element = document.createElement("div");
    this.element.innerHTML = this.render();

    const mountedElement = this.element.firstElementChild;

    // Si container est une string (sélecteur), le chercher dans le document
    if (typeof container === "string") {
      const targetContainer = document.querySelector(container);
      if (targetContainer) {
        targetContainer.appendChild(mountedElement);
      } else {
        logger.error(
          {
            context: "mount-container-not-found",
            containerSelector: container,
            containerType: typeof container,
          },
          "Container not found"
        );
        return null;
      }
    } else if (container && container.appendChild) {
      // Sinon c'est un élément DOM direct
      container.appendChild(mountedElement);
    } else {
      logger.error(
        {
          context: "mount-invalid-container",
          containerType: typeof container,
          containerValue: container,
        },
        "Invalid container provided"
      );
      return null;
    }

    this.element = mountedElement;
    this._bindEvents();

    return this.element;
  }

  _bindEvents() {
    const htmlButton = this.element.querySelector(".execute-btn");
    if (htmlButton) {
      htmlButton.addEventListener("click", () => this.executeTests());
    }

    const excelButton = this.element.querySelector(".export-excel-btn");
    if (excelButton) {
      excelButton.addEventListener("click", () => this.executeExcelExport());
    }
  }

  unmount() {
    if (this.exportProgress) {
      this.exportProgress.hide();
    }

    if (this.successMessage) {
      this.successMessage.unmount();
      this.successMessage = null;
    }

    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
  }
}
